import { flg } from "@aix/webutils/src/ui/logging"
import { AixContentBody, AixContentHeader, Button, toast, Card, CardContent } from "@aix/ui"
import type { IAsset, IVideo } from "@aix/vidmax-shared/src/constants/prismaTypings"
import { ArrowLeft01Icon, SubtitleIcon, PlayCircle02Icon } from "@hugeicons-pro/core-solid-rounded"
import { HugeiconsIcon } from "@hugeicons/react"
import { createFileRoute, useNavigate, Outlet, useMatch } from "@tanstack/react-router"
import { useState } from "react"

import { VideoAssetSelector, TranscriptionProgress } from "~/components/CaptionGenerator"
import { HeaderBreadcrumb } from "~/components/Tools/MediaGeneration/HeaderBreadcrumb"
import CustomHead from "~/components/SEOHead/CustomHead"
import AppLayout from "~/layouts/AppLayout"
import { api } from "~/utils/api"

type CaptionStep = "select" | "transcribe"

function CreateCaptionsPage() {
  const navigate = useNavigate()
  // Check if we're at a child route (e.g., /create/captions/$videoId)
  const isChildRoute = useMatch({ from: "/create/captions/$videoId" as any, shouldThrow: false })

  const [step, setStep] = useState<CaptionStep>("select")
  const [selectedAsset, setSelectedAsset] = useState<IAsset | undefined>()
  const [transcriptionStatus, setTranscriptionStatus] = useState<"idle" | "transcribing" | "complete" | "error">("idle")
  const [transcriptionError, setTranscriptionError] = useState<string>()

  // Fetch previous caption videos
  const { data: previousVideos } = api.captions.listVideos.useQuery({ limit: 10 })

  // Navigate to edit an existing video
  const handleEditVideo = (video: IVideo) => {
    void navigate({ to: "/create/captions/$videoId", params: { videoId: video.id } as any })
  }

  // Transcription mutation
  const { mutate: transcribe, isLoading: isTranscribing } = api.captions.transcribeAsset.useMutation({
    onSuccess: (data) => {
      setTranscriptionStatus("complete")
      toast.success(`Transcription complete! Found ${data.wordCount} words in ${Math.round(data.durationSec)}s video.`)
      // Navigate to editor with video ID in URL
      setTimeout(() => {
        void navigate({ to: "/create/captions/$videoId", params: { videoId: data.videoId } as any })
      }, 1000)
    },
    onError: (error) => {
      setTranscriptionStatus("error")
      setTranscriptionError(error.message)
      toast.error(`Transcription failed: ${error.message}`)
    },
  })

  // Handle asset selection
  const handleAssetSelect = (asset: IAsset | undefined) => {
    setSelectedAsset(asset)
    setTranscriptionStatus("idle")
    setTranscriptionError(undefined)
  }

  // Start transcription
  const handleStartTranscription = () => {
    if (!selectedAsset) {
      toast.error("Please select a video first")
      return
    }

    setStep("transcribe")
    setTranscriptionStatus("transcribing")

    transcribe({
      assetId: selectedAsset.id,
    })
  }

  // Go back to select step
  const handleBackToSelect = () => {
    setStep("select")
    setTranscriptionStatus("idle")
  }

  // If at a child route, render the Outlet (child route content)
  if (isChildRoute) {
    flg.debug("[CreateCaptionsPage] At child route, rendering Outlet")
    return <Outlet />
  }

  flg.debug("[CreateCaptionsPage] At parent route, rendering main content")

  return (
    <>
      <CustomHead title="Add Captions | VidMax" description="Add AI-generated captions to your videos" />

      <AixContentHeader className="flex-col w-full border-b">
        <HeaderBreadcrumb title="Add Captions" />
      </AixContentHeader>

      <AixContentBody className="w-full">
        {/* Step 1: Select Video */}
        {step === "select" && (
          <div className="max-w-2xl mx-auto py-8 px-4">
            <div className="text-center mb-8">
              <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                <HugeiconsIcon icon={SubtitleIcon} size={32} className="text-primary" />
              </div>
              <h1 className="text-2xl font-bold mb-2">Add Captions to Video</h1>
              <p className="text-muted-foreground">
                Select a video from your library and we&apos;ll automatically generate captions using AI
              </p>
            </div>

            <VideoAssetSelector selectedAsset={selectedAsset} onAssetSelect={handleAssetSelect} className="mb-6" />

            <Button
              className="w-full"
              size="lg"
              onClick={handleStartTranscription}
              disabled={!selectedAsset || isTranscribing}
            >
              {isTranscribing ? "Starting..." : "Generate Captions"}
            </Button>

            {/* Previous Videos Section */}
            {previousVideos && previousVideos.videos.length > 0 && (
              <div className="mt-12">
                <h2 className="text-lg font-semibold mb-4">Previous Caption Videos</h2>
                <div className="grid gap-3">
                  {previousVideos.videos.map((video) => (
                    <Card
                      key={video.id}
                      className="cursor-pointer hover:bg-muted/50 transition-colors"
                      onClick={() => handleEditVideo(video)}
                    >
                      <CardContent className="p-4 flex items-center gap-4">
                        <div className="w-12 h-12 rounded bg-primary/10 flex items-center justify-center flex-shrink-0">
                          <HugeiconsIcon icon={PlayCircle02Icon} size={24} className="text-primary" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium truncate">{video.title || "Untitled Caption Video"}</p>
                          <p className="text-sm text-muted-foreground">
                            {new Date(video.createdAt).toLocaleDateString()}
                          </p>
                        </div>
                        <Button variant="ghost" size="sm">
                          Edit
                        </Button>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Step 2: Transcription Progress */}
        {step === "transcribe" && (
          <div className="max-w-2xl mx-auto py-8 px-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleBackToSelect}
              className="mb-4"
              disabled={transcriptionStatus === "transcribing"}
            >
              <HugeiconsIcon icon={ArrowLeft01Icon} size={16} className="mr-1" />
              Back
            </Button>

            <TranscriptionProgress status={transcriptionStatus} error={transcriptionError} className="min-h-[300px]" />

            {transcriptionStatus === "error" && (
              <div className="flex justify-center gap-4 mt-4">
                <Button variant="outline" onClick={handleBackToSelect}>
                  Select Different Video
                </Button>
                <Button onClick={handleStartTranscription}>Try Again</Button>
              </div>
            )}
          </div>
        )}
      </AixContentBody>
    </>
  )
}

function CreateCaptionsPageWrapper() {
  return (
    <AppLayout navCollapsible="icon" navIconFloat>
      <CreateCaptionsPage />
    </AppLayout>
  )
}

export const Route = createFileRoute("/create/captions" as any)({
  component: CreateCaptionsPageWrapper,
})
